import type { BackPattern } from "@/lib/tick-id";
import {
  LOUPE_RADIUS_PX,
  SCALE_MM,
  SCALE_START_PX,
  axisMarks,
  lyingTransform,
  loupeMagnification,
  rowHeightPx,
} from "@/lib/size-scale";
import { TickShape } from "./tick-figure";

/**
 * One tick lying on a millimetre ruler, drawn at its real length.
 *
 * Every row in the key uses the same ruler at the same scale with the zero marks lined
 * up, so the five sizes compare like the bars of a chart: a pinhead sits inside one
 * division, a pea runs past ten. A tick too small to read at real size gets a hand-lens
 * inset beside it, joined by a leader line and labelled with its magnification.
 */
export function ScaleDrawing({
  mm,
  pxPerMm,
  width,
  engorged = false,
  pattern,
  legs,
  darkLegs,
}: {
  mm: number;
  pxPerMm: number;
  width: number;
  engorged?: boolean;
  pattern?: BackPattern;
  legs?: 6 | 8;
  darkLegs?: boolean;
}) {
  const length = mm * pxPerMm;
  const height = rowHeightPx(length, engorged);
  const rulerY = height - 1;
  const cy = (height - 7) / 2;
  const magnification = loupeMagnification(length);
  const loupeCx = SCALE_START_PX + length + 9 + LOUPE_RADIUS_PX;
  const shape = { pattern, legs, darkLegs, engorged };
  const ticks = Array.from({ length: SCALE_MM + 1 }, (_, i) => i);

  return (
    <svg
      width={width}
      height={height}
      aria-hidden
      focusable="false"
      className="block shrink-0 overflow-hidden"
    >
      {/* The ruler: a hairline every millimetre, a taller one every five. */}
      <g className="stroke-foreground" strokeWidth="1" shapeRendering="crispEdges">
        <line
          x1={SCALE_START_PX}
          x2={SCALE_START_PX + SCALE_MM * pxPerMm}
          y1={rulerY}
          y2={rulerY}
          strokeOpacity="0.55"
        />
        {ticks.map((i) => {
          const x = Math.round(SCALE_START_PX + i * pxPerMm) + 0.5;
          const major = i % 5 === 0;
          return (
            <line
              key={i}
              x1={x}
              x2={x}
              y1={major ? 2 : rulerY - 4}
              y2={rulerY}
              strokeOpacity={major ? 0.22 : 0.45}
            />
          );
        })}
      </g>

      <g transform={lyingTransform(SCALE_START_PX, length, cy, engorged)}>
        <TickShape {...shape} />
      </g>
      {/* Below a couple of pixels the drawing antialiases away; keep a speck to point at. */}
      {length < 3 && (
        <circle
          cx={SCALE_START_PX + length / 2}
          cy={cy}
          r={Math.max(0.9, length / 2)}
          className="fill-[oklch(0.32_0.03_60)] dark:fill-rust"
        />
      )}

      {magnification && (
        <g>
          <line
            x1={SCALE_START_PX + length + 2}
            x2={loupeCx - LOUPE_RADIUS_PX}
            y1={cy}
            y2={cy}
            className="stroke-foreground"
            strokeOpacity="0.5"
            strokeWidth="1"
          />
          <circle
            cx={loupeCx}
            cy={cy}
            r={LOUPE_RADIUS_PX}
            className="fill-card stroke-foreground"
            strokeOpacity="0.6"
            strokeWidth="1"
          />
          <g
            transform={lyingTransform(
              loupeCx - (length * magnification) / 2,
              length * magnification,
              cy,
              engorged,
            )}
          >
            <TickShape {...shape} />
          </g>
          <text
            x={loupeCx + LOUPE_RADIUS_PX + 3}
            y={cy + 4}
            className="tabular fill-muted-foreground font-sans"
            fontSize="11"
          >
            ×{magnification}
          </text>
        </g>
      )}
    </svg>
  );
}

/** The numbers under the scale: 0, 5, 10, 15 mm, set at the same zero as every row. */
export function ScaleAxis({ pxPerMm, width }: { pxPerMm: number; width: number }) {
  const marks = axisMarks(SCALE_START_PX + SCALE_MM * pxPerMm + 1, pxPerMm);
  return (
    <svg width={width} height={14} aria-hidden focusable="false" className="block shrink-0">
      {marks.map((mm, i) => (
        <text
          key={mm}
          x={SCALE_START_PX + mm * pxPerMm}
          y={11}
          textAnchor="middle"
          className="tabular fill-muted-foreground font-sans"
          fontSize="11"
        >
          {i === marks.length - 1 ? `${mm} mm` : mm}
        </text>
      ))}
    </svg>
  );
}
